import { useState, useEffect, Fragment } from 'react';
import { useIntl } from 'react-intl';
import { TaskListItem, TaskListDetail } from './TaskListItem';
import ToggleSlider from '../../../core/components/ToggleSlider';

export enum HourlyViewMode {
  Default = 'default',
  Collapsed = 'collapsed',
}

export interface LastFourHoursTasks {
  [key: string]: {
    completed: number;
    total: number;
    summary: string;
  };
}

// Loose type because the component is used by both the legacy task arch
// (TaskCompletion) and the TaskTemplate arch (TaskOccurrence) via the
// optional renderItem/getTaskName props. Treating items as `any` here keeps
// the call sites compatible without dragging both type packages into this
// file or forcing a hand-rolled discriminator.
type HourlyChecklistItem = any;
interface HourlyChecklistProps {
  hourlyTasks: HourlyChecklistItem[];
  lastFourHoursTasks: LastFourHoursTasks;
  viewMode: HourlyViewMode;
  viewModeChange: any;
  setSelectedTask: any;
  filterComplete: (list: HourlyChecklistItem[], invert?: boolean) => HourlyChecklistItem[];
  toggleTaskComplete?: any;
  // Optional render hook for callers that don't want the default
  // TaskListItem-driven rendering (e.g. v2 occurrences rendered as
  // TaskOccurrenceCard). When omitted, legacy rendering is used.
  renderItem?: (task: HourlyChecklistItem, isCompleted: boolean, index: number) => JSX.Element;
  // Optional name accessor for the alphabetical sort applied to the
  // walkthrough list. Defaults to the legacy `task.task.name` path so
  // existing callers behave identically.
  getTaskName?: (task: HourlyChecklistItem) => string;
}

const HourlyChecklist = ({
  hourlyTasks,
  lastFourHoursTasks,
  viewMode,
  viewModeChange,
  setSelectedTask,
  filterComplete,
  toggleTaskComplete,
  renderItem,
  getTaskName,
}: HourlyChecklistProps) => {
  const intl = useIntl();
  const [hourlyViewMode, setHourlyViewMode] = useState<HourlyViewMode>(viewMode);
  const [completed, setCompleted] = useState(filterComplete(hourlyTasks).length);
  const [completedSummary, setCompletedSummary] = useState(`${completed}/${hourlyTasks.length}`);
  const [displayCompleted, setDisplayCompleted] = useState(false);

  const formatHour = (hour: string) => {
    const hourNumber = parseInt(hour);
    const period = hourNumber >= 12 ? 'pm' : 'am';
    const displayHour = hourNumber === 0 ? 12 : hourNumber > 12 ? hourNumber - 12 : hourNumber;
    return `${displayHour} ${period}`;
  };

  const handleCollapseButton = () => {
    setHourlyViewMode(HourlyViewMode.Collapsed);
  };

  const formatLastFourHoursTasks = () => {
    if (!lastFourHoursTasks) {
      return [];
    }
    const keys = Object.keys(lastFourHoursTasks);
    return keys.sort().map((key: string) => {
      const task = lastFourHoursTasks[key as keyof LastFourHoursTasks];
      return { hour: key, completed: task.completed, total: task.total, summary: task.summary };
    });
  };

  const getHourStatus = (tasksCompleted: number, total: number) => {
    if (total > 0) {
      const percentage = tasksCompleted / total;
      if (percentage < 0.5) {
        return 'bad';
      }
      if (percentage >= 0.5 && percentage < 1) {
        return 'ok';
      }
      if (percentage >= 1) {
        return 'good';
      }
    }
    return '';
  };

  // Keyboard shortcut for toggling hourly view mode
  const handleKeyPress = (event: KeyboardEvent) => {
    if (event.ctrlKey && event.key === 'h') {
      event.preventDefault();
      handleCollapseButton();
    }
  };

  const setViewMode = () => {
    // if tablet or mobile, hourly will be collapsed on page load
    if (window.innerWidth > 1000) {
      setHourlyViewMode(HourlyViewMode.Default);
    } else {
      setHourlyViewMode(HourlyViewMode.Collapsed);
    }
  };

  // Add keyboard event listener
  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [hourlyViewMode]);

  useEffect(() => {
    if (viewModeChange) viewModeChange(hourlyViewMode);
  }, [hourlyViewMode]);

  useEffect(() => {
    setHourlyViewMode(viewMode);
  }, [viewMode]);

  useEffect(() => {
    setCompleted(filterComplete(hourlyTasks).length);
  }, [hourlyTasks]);

  useEffect(() => {
    setCompletedSummary(`${completed}/${hourlyTasks.length}`);
  }, [completed, hourlyTasks.length]);

  useEffect(() => {
    setViewMode();
  }, []);

  return (
    <div className="tasks-hourly">
      <div className="hourly-toggle-container">
        <button
          className="task-drawer-toggle-button"
          onClick={handleCollapseButton}
          title={'Toggle hourly checklist'}
        >
          <i className="uil uil-angle-right"></i>
        </button>
        <ToggleSlider
          leftLabel={intl.formatMessage({ id: 'walkthrough' })}
          rightLabel={intl.formatMessage({ id: 'completed' })}
          leftCallback={() => setDisplayCompleted(false)}
          rightCallback={() => setDisplayCompleted(true)}
        />
      </div>
      <div className="hour-completion-list">
        {formatLastFourHoursTasks().map((stat, index) => {
          const isCurrentHour = index === 3; // Fourth hour is current
          const status = getHourStatus(isCurrentHour ? completed : stat.completed, stat.total);
          return (
            <Fragment key={stat.hour}>
              <div className="hour">
                <div className={`hour-check${status != '' ? ' status ' + status : ''}`}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    height="24px"
                    viewBox="0 -960 960 960"
                    width="24px"
                  >
                    <path d="M382-240 154-468l57-57 171 171 367-367 57 57-424 424Z" />
                  </svg>
                </div>
                <div>
                  <div>{isCurrentHour ? completedSummary : stat.summary}</div>
                </div>
                <span>{formatHour(stat.hour)}</span>
              </div>
              {!isCurrentHour && <div className="hour-line"></div>}
            </Fragment>
          );
        })}
      </div>
      <div className="hourly-list">
        {displayCompleted
          ? filterComplete(hourlyTasks).map((task: HourlyChecklistItem, i: number) =>
              renderItem ? (
                renderItem(task, true, i)
              ) : (
                <TaskListItem
                  taskCompletion={task}
                  detailType={TaskListDetail.Subtitle}
                  displayOptionsMenu={false}
                  displayTimeInfo={false}
                  key={`completed-hourly-${i}`}
                  onOpen={() => setSelectedTask(task)}
                  toggleCompletion={toggleTaskComplete}
                  className="tabular"
                />
              )
            )
          : filterComplete(hourlyTasks, true)
              .sort((a: HourlyChecklistItem, b: HourlyChecklistItem) => {
                const nameA = getTaskName ? getTaskName(a) : a?.task?.name ?? '';
                const nameB = getTaskName ? getTaskName(b) : b?.task?.name ?? '';
                return String(nameA).localeCompare(String(nameB));
              })
              .map((task: HourlyChecklistItem, i: number) =>
                renderItem ? (
                  renderItem(task, false, i)
                ) : (
                  <TaskListItem
                    taskCompletion={task}
                    onOpen={() => setSelectedTask(task)}
                    detailType={TaskListDetail.Subtitle}
                    displayOptionsMenu={false}
                    displayTimeInfo={false}
                    key={`incomplete-hourly-${i}`}
                    toggleCompletion={toggleTaskComplete}
                    className="tabular"
                  />
                )
              )}
        {hourlyTasks.length == 0 && (
          <p className="tasks-not-found center">
            {intl.formatMessage({ id: 'hourlyTasksMissing' })}
          </p>
        )}
      </div>
    </div>
  );
};

export default HourlyChecklist;
